import type {
  VehicleJourneyCatalogs,
  YardCarrierGroupPayload,
  YardCarrierGroupRule,
  YardCarrierGroupRulePayload,
  YardCatalogItem,
  YardCompanyStepExceptionConfiguration,
  YardFlow,
  YardPaginatedResponse,
  YardPaginatedSearch,
  YardStepCatalogItem,
  YardFlowCompositionPayload,
  YardFlowHeaderPayload,
  YardStepPayload,
} from 'contracts/VehicleJourney';
import type { YardRole } from 'contracts/YardRoles';
import { api } from 'services';

// The legacy API uses /dashboard as its base, while the YMS module is mounted at the root.
const apiRoot = (api.defaults.baseURL || '').replace(/\/dashboard\/?$/, '');
const ymsPath = `${apiRoot}/api/v1/yms-portaria`;

const responseItems = <T>(response: T[] | { data: T[] }): T[] =>
  Array.isArray(response) ? response : response.data;

const getFlows = async (): Promise<YardFlow[]> => {
  const { data } = await api.get(`${ymsPath}/yard-flows`, {
    params: { limit: 100, orderBy: 'name', direction: 'asc' },
  });
  const flows = responseItems<YardFlow>(data);

  return Promise.all(
    flows.map(async ({ id }) => {
      const response = await api.get<YardFlow>(`${ymsPath}/yard-flows/${id}`);
      return response.data;
    })
  );
};

const getRoles = async (): Promise<YardRole[]> => {
  const { data } = await api.get('yard-roles', {
    params: { asList: true, active: true, orderBy: 'name', direction: 'asc' },
  });
  return responseItems<YardRole>(data);
};

const getCatalogs = async (): Promise<VehicleJourneyCatalogs> => {
  const [warehouses, orderTypes, carriers, carrierGroups, steps] =
    await Promise.all([
      api.get('/warehouses', {
        params: { asList: 1, orderBy: 'tradeName', direction: 'asc' },
      }),
      api.get('/order-types', {
        params: { asList: 1, orderBy: 'name', direction: 'asc' },
      }),
      api.get('/carriers', {
        params: { asList: 1, orderBy: 'tradeName', direction: 'asc' },
      }),
      api.get<YardCatalogItem[]>(`${ymsPath}/yard-carrier-groups`, {
        params: { asList: true, active: true },
      }),
      api.get<YardStepCatalogItem[]>(`${ymsPath}/yard-steps`, {
        params: { asList: true, active: true },
      }),
    ]);

  return {
    warehouses: responseItems<any>(warehouses.data).map((warehouse) => ({
      id: warehouse.id,
      name: warehouse.tradeName,
    })),
    orderTypes: responseItems<any>(orderTypes.data).map((orderType) => ({
      id: orderType.id,
      name: orderType.name,
    })),
    carriers: responseItems<any>(carriers.data).map((carrier) => ({
      id: carrier.id,
      name: carrier.tradeName,
    })),
    carrierGroups: responseItems<YardCatalogItem>(carrierGroups.data),
    steps: responseItems<YardStepCatalogItem>(steps.data),
  };
};

const getCompanyStepExceptions = async (
  params: YardPaginatedSearch
): Promise<YardPaginatedResponse<YardCompanyStepExceptionConfiguration>> => {
  const { data } = await api.get<
    YardPaginatedResponse<YardCompanyStepExceptionConfiguration>
  >(`${ymsPath}/yard-company-step-exceptions`, { params });
  return data;
};

const getYardStepsPage = async (
  params: YardPaginatedSearch
): Promise<YardPaginatedResponse<YardStepCatalogItem>> => {
  const { data } = await api.get<YardPaginatedResponse<YardStepCatalogItem>>(
    `${ymsPath}/yard-steps`,
    {
      params: {
        ...params,
        active: true,
        orderBy: 'name',
        direction: 'asc',
      },
    }
  );
  return data;
};

const replaceCompanyStepExceptions = async (
  companyId: number,
  yardStepIds: number[]
): Promise<void> => {
  await api.put(`${ymsPath}/yard-company-step-exceptions/${companyId}`, {
    yardStepIds,
  });
};

const getCarrierGroups = async (): Promise<YardCatalogItem[]> => {
  const { data } = await api.get(`${ymsPath}/yard-carrier-groups`, {
    params: { asList: true },
  });
  return responseItems<YardCatalogItem>(data);
};

const createFlow = async (
  payload: YardFlowHeaderPayload
): Promise<YardFlow> => {
  const { data } = await api.post(`${ymsPath}/yard-flows`, payload);
  return data;
};

const createStep = async (
  payload: YardStepPayload
): Promise<YardStepCatalogItem> => {
  const { data } = await api.post(`${ymsPath}/yard-steps`, payload);
  return data;
};

const createCarrierGroup = async (
  payload: YardCarrierGroupPayload
): Promise<YardCatalogItem> => {
  const { data } = await api.post(`${ymsPath}/yard-carrier-groups`, payload);
  return data;
};

const updateCarrierGroup = async (
  id: number,
  payload: YardCarrierGroupPayload
): Promise<YardCatalogItem> => {
  const { data } = await api.put(
    `${ymsPath}/yard-carrier-groups/${id}`,
    payload
  );
  return data;
};

const getCarrierGroupRules = async (
  groupId: number
): Promise<YardCarrierGroupRule[]> => {
  const { data } = await api.get(
    `${ymsPath}/yard-carrier-groups/${groupId}/rules`,
    { params: { asList: true } }
  );
  return responseItems<YardCarrierGroupRule>(data);
};

const createCarrierGroupRule = async (
  groupId: number,
  payload: YardCarrierGroupRulePayload
): Promise<YardCarrierGroupRule> => {
  const { data } = await api.post(
    `${ymsPath}/yard-carrier-groups/${groupId}/rules`,
    payload
  );
  return data;
};

const updateCarrierGroupRule = async (
  groupId: number,
  id: number,
  payload: YardCarrierGroupRulePayload
): Promise<YardCarrierGroupRule> => {
  const { data } = await api.put(
    `${ymsPath}/yard-carrier-groups/${groupId}/rules/${id}`,
    payload
  );
  return data;
};

const updateFlow = async (
  id: number,
  payload: YardFlowHeaderPayload
): Promise<YardFlow> => {
  const { data } = await api.put(`${ymsPath}/yard-flows/${id}`, payload);
  return data;
};

const replaceComposition = async (
  id: number,
  payload: YardFlowCompositionPayload
): Promise<YardFlow> => {
  const { data } = await api.put(
    `${ymsPath}/yard-flows/${id}/composition`,
    payload
  );
  return data;
};

const deleteFlow = async (id: number): Promise<void> => {
  await api.delete(`${ymsPath}/yard-flows/${id}`);
};

export function useVehicleJourneyConfiguration() {
  return {
    getFlows,
    getCatalogs,
    getRoles,
    getCompanyStepExceptions,
    getYardStepsPage,
    replaceCompanyStepExceptions,
    getCarrierGroups,
    createFlow,
    createStep,
    createCarrierGroup,
    updateFlow,
    updateCarrierGroup,
    getCarrierGroupRules,
    createCarrierGroupRule,
    updateCarrierGroupRule,
    replaceComposition,
    deleteFlow,
  };
}
